/**
 * Amenity catalog validators — SCRUM-137 / DS-010-1.
 *
 * HTTP-boundary + contract Zod schemas for the platform-global amenity
 * catalog (OTA-seeded reference data). Composed from:
 *   - the slug policy (`AMENITY_SLUG_MIN_LENGTH` / `SLUG_MAX_LENGTH` /
 *     `SLUG_PATTERN` / `SLUG_PATTERN_MESSAGE`) reused from `../policies/slug.js`
 *     — note the amenity minimum is 2 (not the identity floor of 3, SCRUM-262),
 *     so the curated catalog's `tv` slug validates, and
 *   - the amenity policy (`AMENITY_CATEGORY_VALUES` / `AMENITY_LABEL_MIN_LENGTH` /
 *     `AMENITY_LABEL_MAX_LENGTH`) from `../policies/amenity.js`.
 *
 * The same policy constants back the property domain VOs and the
 * `property.amenity_catalog` DB CHECK constraints, so the Zod schema, the
 * `AmenityLabel`/`AmenityCategory`/`AmenitySlug` VOs, and the table CHECKs
 * share one source of truth.
 *
 * The brand pattern mirrors `language-code.ts` (`as const` tuple →
 * `z.enum().brand()` → `z.infer`) and the slug brand at `slug.ts`.
 *
 * Label length is validated by Unicode codepoint count (`[...v].length`), NOT
 * by `.min()`/`.max()` which count UTF-16 code units — so a multi-codepoint
 * emoji counts as 1, matching the `AmenityLabel` VO and the
 * `char_length(label)` DB CHECK.
 */
import { z } from 'zod';
import { AMENITY_SLUG_MIN_LENGTH, SLUG_MAX_LENGTH, SLUG_PATTERN, SLUG_PATTERN_MESSAGE, } from '../policies/slug.js';
import { AMENITY_CATEGORY_VALUES, AMENITY_PROVENANCE_VALUES, AMENITY_LABEL_MIN_LENGTH, AMENITY_LABEL_MAX_LENGTH, } from '../policies/amenity.js';
export const amenityCategorySchema = z.enum(AMENITY_CATEGORY_VALUES);
/**
 * Provenance enum (SCRUM-262 / CQ-262-3) — `confirmed-official` |
 * `secondary-sourced`. Shares its source of truth with the
 * `AMENITY_PROVENANCE_VALUES` policy const, the catalog `provenance` DB
 * CHECK, and the catalog GET response model.
 */
export const amenityProvenanceSchema = z.enum(AMENITY_PROVENANCE_VALUES);
export const amenitySlugSchema = z
    .string()
    .min(AMENITY_SLUG_MIN_LENGTH)
    .max(SLUG_MAX_LENGTH)
    .regex(SLUG_PATTERN, SLUG_PATTERN_MESSAGE)
    .brand();
/**
 * Label is validated by Unicode codepoint count, NOT `.min`/`.max` (UTF-16
 * units). Two distinct checks, matching `amenity.spec.ts`:
 *   - non-empty: the TRIMMED value must have ≥ MIN codepoints (a
 *     whitespace-only string collapses to '' and is rejected).
 *   - cap: the RAW (untrimmed) value must have ≤ MAX codepoints — a 121-raw
 *     string with a trailing space fails the cap even though it trims to 120.
 * This is deliberately stricter than the domain `AmenityLabel` VO, which
 * trims-then-caps; at the HTTP boundary the raw payload length is what counts.
 */
export const amenityLabelSchema = z
    .string()
    .refine((v) => [...v.trim()].length >= AMENITY_LABEL_MIN_LENGTH, {
    message: 'Label must not be empty or whitespace-only',
})
    .refine((v) => [...v].length <= AMENITY_LABEL_MAX_LENGTH, {
    message: `Label must be at most ${AMENITY_LABEL_MAX_LENGTH} characters`,
});
export const amenityCatalogItemSchema = z.object({
    // `id` is the catalog row's UUID. It is optional at the schema boundary
    // because callers compose catalog items before the DB assigns an id (the
    // seed/contract shape). When present it must be a UUID.
    id: z.string().uuid().optional(),
    slug: amenitySlugSchema,
    label: amenityLabelSchema,
    category: amenityCategorySchema,
    source: z.string().min(1),
    isActive: z.boolean(),
    provenance: amenityProvenanceSchema,
});
//# sourceMappingURL=amenity.js.map